import React, { useState } from 'react';
import { Button, Form, FormInput, FormInputType } from '@whoop/web-components';
import CenteredCardLayout from 'layouts/CenteredCardLayout';
import FormError from 'components/shared/FormError';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from 'config';
import auth from 'services/auth';
import styles from './index.module.scss';

function Login() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('login');
  const [authenticating, setAuthenticating] = useState(false);
  const [authError, setAuthError] = useState(undefined);

  const doLogin = async (v: Record<string, any>) => {
    setAuthenticating(true);
    setAuthError(undefined);

    // Determine if we are in Discourse SSO or WHOOP SSO
    const sso = searchParams.get('sso');

    try {
      if (sso) {
        const loginResponse = await auth.ssoLogin({
          email: v.email,
          password: v.password,
          sso: searchParams.get('sso'),
          sig: searchParams.get('sig'),
          return_url: searchParams.get('return_sso_url'),
        });
        window.location.assign(loginResponse.redirect_url);
      } else {
        const loginResponse = await auth.login({
          email: v.email,
          password: v.password,
          loginChallenge: searchParams.get('login_challenge'),
        });
        window.location.replace(loginResponse.redirect_url);
      }
    } catch (e) {
      setAuthError(t('invalidEmailOrPass'));
    } finally {
      setAuthenticating(false);
    }
  };

  return (
    <CenteredCardLayout>
      <h2 className={styles.signInText}>{t('signInToWhoop')}</h2>
      {authError && <FormError message={authError} data-testid="form-error" />}
      <Form onSubmit={(v: any) => doLogin(v)} className={styles.formContainer}>
        <FormInput
          required={{ value: true, message: t('emailIsRequired') }}
          name="email"
          label={t('email')}
          type={FormInputType.TEXT}
          className={styles.formElement}
          data-testid="email-input"
        />

        <FormInput
          required={{ value: true, message: t('passwordIsRequired') }}
          name="password"
          label={t('password')}
          type={FormInputType.PASSWORD}
          className={styles.formElement}
          data-testid="password-input"
        />

        <Button
          label={t('login')}
          variant="normal"
          type="submit"
          className={styles.signInButton}
          disabled={authenticating}
          data-testid="login-button"
        />
      </Form>
      <a
        href={config.forgotPassword}
        className={styles.forgotPasswordButton}
        target="_blank"
        rel="noreferrer"
        data-testid="forgot-password-button"
      >
        {t('forgot')}
      </a>
    </CenteredCardLayout>
  );
}

export default Login;
